// ArchiveTab (#2140) — read-only "Archive" tab: lists return snapshots
//   newest-first, and opens any one as a frozen request via the archive
//   URL param. Opening a snapshot drives the drawer into a full read-only
//   takeover (`ArchiveSnapshotView`) rendered by `RequestDrawer`; this tab
//   is only ever the list (it never renders while a snapshot is open).

import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, LoadingState } from '../../../../ui/index.js';
import type { RequestReturnSnapshotListItem } from '@magimp/shared';
import { formatDateTime } from '../../format.js';
import { useRequestUrlState } from '../../useRequestUrlState.js';

export function ArchiveTab({
  snapshots,
  isLoading,
  isError,
  onRetry,
}: {
  snapshots: RequestReturnSnapshotListItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation(['requests', 'common']);
  const { openSnapshot } = useRequestUrlState();

  if (isLoading) return <LoadingState />;
  if (isError) {
    return <ErrorState text={t('requests:archive.loadError')} onRetry={onRetry} />;
  }

  // Newest-first by `returnedAt` (ISO strings sort lexicographically).
  const sortedSnapshots = [...(snapshots ?? [])].sort((a, b) =>
    b.returnedAt.localeCompare(a.returnedAt),
  );
  if (sortedSnapshots.length === 0) {
    return (
      <div data-testid="request-archive-tab-empty">
        <EmptyState icon="archive" title={t('requests:archive.empty')} />
      </div>
    );
  }

  return (
    <ul
      data-testid="request-archive-tab-list"
      style={{
        margin: 0,
        padding: 12,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {sortedSnapshots.map((s) => (
        <li key={s.id} data-snapshot-id={s.id}>
          <button
            type="button"
            data-testid="request-archive-tab-row"
            onClick={() => openSnapshot(s.id)}
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: '8px 10px',
              borderRadius: 'var(--r-2)',
              border: '1px solid var(--border-1)',
              background: 'var(--bg-1)',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--fg-1)' }}>
              {t('requests:archive.returnedAt', { date: formatDateTime(s.returnedAt) })}
            </span>
            {s.reason && (
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--fg-2)',
                  overflowWrap: 'anywhere',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {s.reason}
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
